import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Clock3, FileSearch, ShieldAlert, XCircle } from "lucide-react";
import { Link, useRoute } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PageContainer } from "@/components/layout/PageContainer";
import { unwrapPayload } from "@/lib/queryClient";
import { buildLoginPath } from "@/lib/auth-navigation";

type Claim = {
  id: string;
  status: "claim_pending" | "approved" | "rejected" | "disputed" | "revoked";
  verification_method: string;
  verification_target_masked: string | null;
  decision_reason: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
};

type ClaimStatusResponse = {
  business: { id: string; name: string; claimStatus: string; publicationStatus: string };
  items: Claim[];
};

const statusContent = {
  claim_pending: { label: "검토 중", description: "관리자가 소유권 증빙과 기존 업체 정보를 확인하고 있습니다.", icon: Clock3, className: "text-amber-600" },
  approved: { label: "소유권 승인", description: "업체 소유권이 확인되었습니다. 업체 정보를 검토하고 프로필을 완성해 주세요.", icon: CheckCircle2, className: "text-emerald-600" },
  rejected: { label: "요청 거절", description: "제출 정보만으로 소유권을 확인하지 못했습니다. 처리 사유를 확인해 주세요.", icon: XCircle, className: "text-destructive" },
  disputed: { label: "소유권 분쟁 검토", description: "기존 소유자 또는 다른 신청과 충돌하여 추가 확인 중입니다.", icon: ShieldAlert, className: "text-amber-600" },
  revoked: { label: "소유권 회수", description: "승인된 소유권이 회수되었습니다. 사유를 확인해 주세요.", icon: XCircle, className: "text-destructive" },
} as const;

const methodLabel: Record<string, string> = {
  domain_email: "업체 도메인 이메일",
  phone_otp: "전화 OTP",
  phone_callback: "전화 콜백",
  abn_document: "ABN·사업자 서류",
  manual_review: "관리자 수동 검토",
};

export default function BusinessClaimStatus() {
  const [match, params] = useRoute<{ id: string }>("/business-directory/:id/claim");
  const { user, isAuthenticated } = useAuth();
  const businessId = match && params ? params.id : "";
  const claimQuery = useQuery({
    queryKey: ["business-claim-status", businessId, user?.uid],
    enabled: !!businessId && !!user && isAuthenticated,
    queryFn: async () => {
      if (!user) throw new Error("로그인이 필요합니다.");
      const token = await user.getIdToken();
      const response = await fetch(`/api/businesses/${encodeURIComponent(businessId)}/claims`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("소유권 요청 상태를 불러오지 못했습니다.");
      return unwrapPayload<ClaimStatusResponse>(await response.json());
    },
  });

  if (!isAuthenticated) {
    return <PageContainer size="read"><Card><CardHeader><CardTitle>로그인 필요</CardTitle><CardDescription>본인이 신청한 업체 소유권 상태는 로그인 후 확인할 수 있습니다.</CardDescription></CardHeader><CardContent><Link href={buildLoginPath()}><Button>로그인</Button></Link></CardContent></Card></PageContainer>;
  }

  const latest = claimQuery.data?.items?.[0];
  const content = latest ? statusContent[latest.status] : null;
  const StatusIcon = content?.icon || FileSearch;

  return (
    <PageContainer size="read">
      <div className="space-y-6 py-8">
        <div><h1 className="text-2xl font-bold">업체 소유권 요청</h1><p className="text-muted-foreground">{claimQuery.data?.business.name || "업체 정보를 불러오는 중입니다."}</p></div>
        <Card>
          <CardHeader>
            <div className="flex items-start gap-3"><StatusIcon className={`mt-1 h-7 w-7 ${content?.className || "text-muted-foreground"}`} /><div><CardTitle>{content?.label || (claimQuery.isLoading ? "상태 확인 중" : "신청 내역 없음")}</CardTitle><CardDescription className="mt-1">{content?.description || "이 업체에 제출한 소유권 요청이 없습니다."}</CardDescription></div></div>
          </CardHeader>
          {latest && <CardContent className="space-y-4">
            <div className="grid gap-3 rounded-lg border p-4 sm:grid-cols-2">
              <div><div className="text-xs text-muted-foreground">신청 상태</div><Badge className="mt-1" variant={latest.status === "rejected" || latest.status === "revoked" ? "destructive" : "secondary"}>{content?.label}</Badge></div>
              <div><div className="text-xs text-muted-foreground">확인 방법</div><div className="mt-1 text-sm font-medium">{methodLabel[latest.verification_method] || latest.verification_method}</div></div>
              <div><div className="text-xs text-muted-foreground">신청일</div><div className="mt-1 text-sm">{new Date(latest.created_at).toLocaleString("ko-KR")}</div></div>
              <div><div className="text-xs text-muted-foreground">업체 공개 상태</div><div className="mt-1 text-sm">{claimQuery.data?.business.publicationStatus}</div></div>
            </div>
            {latest.decision_reason && <div className="rounded-lg bg-muted p-4"><div className="text-sm font-medium">관리자 처리 사유</div><p className="mt-1 text-sm text-muted-foreground">{latest.decision_reason}</p></div>}
            <div className="flex flex-wrap gap-2">
              {latest.status === "approved" && <Link href={`/businesses/${businessId}/manage`}><Button>업체 관리 시작하기</Button></Link>}
              {claimQuery.data?.business.publicationStatus === "published" && <Link href={`/business-directory/${businessId}`}><Button variant="outline">공개 페이지 보기</Button></Link>}
              <Link href="/business-directory"><Button variant="ghost">업체 목록</Button></Link>
            </div>
          </CardContent>}
        </Card>
      </div>
    </PageContainer>
  );
}
